"use client";
import BlogCard, { BlogSkeleton } from "@/components/views/blog/BlogCard";
import InfiniteScroll from "@/components/InfiniteScroll";
import { use, useEffect, useRef, useState } from "react";
import blogFetcher from "./blogFetcher";
import { useBlogContext } from "@/context/BlogContext";
import { DEFAULT_DATA_LIMIT } from "@/@core/constants/pagination";
import { useSearchParams } from "next/navigation";

interface Props {
  initialData: any;
}

export default function BlogClientSection(props: Props) {
  const [blogs, setBlogs] = useState(props.initialData);
  const [hasMore, setHasMore] = useState(
    props.initialData?.length >= DEFAULT_DATA_LIMIT,
  );
  const [isLoading, setIsLoading] = useState(false);
  const { blogFilterId } = useBlogContext();
  const isFirstRender = useRef(true);

  const next = (page: number) => {
    if (isLoading) return;

    setIsLoading(true);
    blogFetcher({
      serviceId: blogFilterId,
      index: page,
    }).then((r) => {
      setIsLoading(false);
      setBlogs((prev: any) => {
        return [...prev, ...r];
      });

      if (r.length === 0 || r.length < DEFAULT_DATA_LIMIT) setHasMore(false);
    });
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    blogFetcher({
      serviceId: blogFilterId,
      index: 0,
    }).then((data) => {
      setIsLoading(false);
      setBlogs(data);

      const moreBlogsAvailable =
        data.length === 0 || data.length < DEFAULT_DATA_LIMIT;
      setHasMore(!moreBlogsAvailable);
    });
  }, [blogFilterId]);

  return (
    <InfiniteScroll
      hasMore={hasMore}
      next={next}
      loading={<BlogLoading />}
      isLoading={isLoading}
      skeletonElementId="blog-card-skeleton"
      dataLength={blogs?.length || 0}
    >
      <div className="grid justify-items-center grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-5 gap-y-6 py-4">
        {Array.isArray(blogs) &&
          blogs.map((blog: Blog, index: number) => {
            return (
              <BlogCard
                titleHeadingLevel="h2"
                descriptions={blog.descriptions}
                key={index}
                id={blog.id}
              />
            );
          })}
      </div>
    </InfiniteScroll>
  );
}

export function BlogLoading() {
  return (
    <div
      id="blog-card-skeleton"
      className="grid justify-items-center grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-5 gap-y-6 py-4"
    >
      {new Array(9).fill(0).map((_, index) => {
        return <BlogSkeleton key={index} />;
      })}
    </div>
  );
}
